import { useCallback, useSyncExternalStore } from 'react';

/**
 * useMediaQuery -- Reactive CSS media query hook.
 *
 * Returns `true` when the given media query matches. Subscribes to changes
 * via the `matchMedia` API so the component re-renders on breakpoint
 * transitions.
 *
 * Uses `useSyncExternalStore` for tear-free reads of the browser media query.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => {};
      }
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onStoreChange);
      return () => mql.removeEventListener('change', onStoreChange);
    },
    [query],
  );

  const getSnapshot = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(query).matches;
  }, [query]);

  const getServerSnapshot = useCallback(() => false, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
